import { useEffect, useRef, useState } from 'react';
import {
  Cloud,
  CheckCircle2,
  XCircle,
  RefreshCw,
  AlertTriangle,
  FileText,
  SkipForward,
  Square,
} from 'lucide-react';
import Pill from '../components/ui/Pill.jsx';
import Modal from '../components/ui/Modal.jsx';
import { api } from '../api/client.js';
import { formatDateFr } from '../lib/dates.js';

const EVENT_STYLES = {
  started: { color: 'var(--primary)', icon: Cloud },
  file_start: { color: 'var(--text-muted)', icon: FileText },
  file_uploaded: { color: 'var(--success)', icon: CheckCircle2 },
  file_skipped: { color: 'var(--text-muted)', icon: SkipForward },
  file_error: { color: 'var(--danger)', icon: XCircle },
  completed: { color: 'var(--success)', icon: CheckCircle2 },
};

const DEFAULT_DRIVE_ID = '1pKyS2N7brhB1kVU1SMGRoo-rUtEQ1qmW';

export default function DriveView({ params = {} }) {
  const [driveId, setDriveId] = useState(DEFAULT_DRIVE_ID);
  const [folderName, setFolderName] = useState(null);
  const [folderState, setFolderState] = useState('idle'); // idle | checking | valid | invalid
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState(params.jobId ?? null);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadEvents, setUploadEvents] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [currentFile, setCurrentFile] = useState(null);
  const abortRef = useRef(null);

  // Charge les jobs complétés
  useEffect(() => {
    api.cra
      .listJobs({ limit: 20 })
      .then(({ jobs }) => {
        const completed = jobs.filter((j) => j.status === 'COMPLETED' || j.status === 'PARTIAL');
        setJobs(completed);
        // Si on arrive avec un jobId pré-sélectionné mais qu'il n'est pas encore
        // dans la liste (job tout juste terminé), on l'ajoute pour le rendre visible
        if (params.jobId && !completed.some((j) => j.id === params.jobId)) {
          api.cra.getJob(params.jobId).then((j) => setJobs((prev) => [j, ...prev])).catch(() => {});
        }
      })
      .catch(() => setJobs([]));
  }, [params.jobId]);

  // Validation de l'ID Drive (debounce léger : 400ms)
  useEffect(() => {
    if (!driveId) {
      setFolderState('idle');
      return undefined;
    }
    setFolderState('checking');
    const t = setTimeout(() => {
      api.drive
        .getFolderName(driveId)
        .then(({ name }) => {
          setFolderName(name);
          setFolderState(name ? 'valid' : 'invalid');
        })
        .catch(() => setFolderState('invalid'));
    }, 400);
    return () => clearTimeout(t);
  }, [driveId]);

  const handleUpload = async () => {
    setConfirmOpen(false);
    setUploading(true);
    setUploadResult(null);
    setUploadEvents([]);
    setUploadProgress({ done: 0, total: 0 });
    setCurrentFile(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const last = await api.drive.uploadJobStream(
        selectedJobId,
        { parentFolderId: driveId, replaceExisting },
        {
          signal: controller.signal,
          onEvent: (event) => {
            setUploadEvents((prev) => [...prev, event]);
            if (event.type === 'started') {
              setUploadProgress({ done: 0, total: event.total ?? 0 });
            } else if (event.type === 'file_start') {
              setCurrentFile(event.fileName ?? null);
            } else if (
              event.type === 'file_uploaded' ||
              event.type === 'file_skipped' ||
              event.type === 'file_error'
            ) {
              setUploadProgress({
                done: event.done ?? 0,
                total: event.total ?? 0,
              });
            }
          },
        },
      );
      setCurrentFile(null);
      if (last?.type === 'completed') {
        setUploadResult({ ok: true, ...last });
      } else {
        setUploadResult({ ok: true, uploaded: 0, skipped: 0, errors: 0 });
      }
    } catch (err) {
      if (controller.signal.aborted) {
        setUploadResult({ ok: false, error: 'Upload interrompu par l\'utilisateur' });
      } else {
        setUploadResult({ ok: false, error: err.message });
      }
    } finally {
      abortRef.current = null;
      setUploading(false);
      setCurrentFile(null);
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Dossier Google Drive cible</h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ ...labelStyle, flex: '1 1 360px' }}>
            ID du dossier Drive
            <input
              type="text"
              value={driveId}
              maxLength={64}
              onChange={(e) => setDriveId(e.target.value.trim())}
              placeholder="ex: 1pKyS2N7brhB..."
              style={inputStyle}
            />
          </label>
        </div>
        <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {folderState === 'checking' && (
            <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <RefreshCw size={14} className="spin" /> Vérification…
            </span>
          )}
          {folderState === 'valid' && (
            <span style={{ color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <CheckCircle2 size={16} /> Dossier : <strong>{folderName}</strong>
            </span>
          )}
          {folderState === 'invalid' && (
            <span style={{ color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <AlertTriangle size={16} /> Dossier inaccessible ou ID invalide
            </span>
          )}
        </div>
      </section>

      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Génération à uploader</h2>
        {jobs.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>
            Aucun job de génération disponible. Lancez d'abord une génération depuis l'onglet
            "Création des PDFs".
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => setSelectedJobId(j.id)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 1rem',
                  background: selectedJobId === j.id ? 'var(--primary)' : 'var(--glass)',
                  color: selectedJobId === j.id ? '#fff' : 'var(--text)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {j.startDate?.slice(0, 10)} → {j.endDate?.slice(0, 10)}
                  </div>
                  <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>
                    {j.totalPdfs} PDF · {j.totalConsultants} consultants ·{' '}
                    {formatDateFr(j.startedAt)}
                  </div>
                </div>
                <span
                  style={{
                    padding: '0.25rem 0.6rem',
                    borderRadius: 'var(--radius-pill)',
                    fontSize: '0.75rem',
                    background:
                      j.status === 'COMPLETED' ? 'var(--success)' : 'var(--warning)',
                    color: '#fff',
                  }}
                >
                  {j.status}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Options d'upload</h2>
        <Pill active={replaceExisting} onClick={() => setReplaceExisting((v) => !v)}>
          Remplacer les fichiers existants
        </Pill>
      </section>

      <section style={cardStyle}>
        <h2 style={sectionTitleStyle}>Lancement</h2>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={!selectedJobId || folderState !== 'valid' || uploading}
            style={primaryButtonStyle(!selectedJobId || folderState !== 'valid' || uploading)}
          >
            {uploading ? <RefreshCw size={16} className="spin" /> : <Cloud size={16} />}
            {uploading ? 'Upload en cours…' : "Lancer l'upload Drive"}
          </button>
          {uploading && (
            <button onClick={handleStop} style={dangerButtonStyle}>
              <Square size={16} /> Arrêter
            </button>
          )}
        </div>

        {uploadProgress.total > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <div
              style={{
                height: '8px',
                background: 'var(--glass)',
                borderRadius: 'var(--radius-pill)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${(uploadProgress.done / Math.max(uploadProgress.total, 1)) * 100}%`,
                  height: '100%',
                  background: 'var(--primary)',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {uploadProgress.done} / {uploadProgress.total} fichier(s) traité(s)
              {currentFile && uploading && <> · en cours : <strong>{currentFile}</strong></>}
            </p>
          </div>
        )}

        {uploadResult && (
          <div
            style={{
              marginTop: '1rem',
              padding: '0.75rem 1rem',
              borderRadius: 'var(--radius-md)',
              background: uploadResult.ok ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              color: uploadResult.ok ? 'var(--success)' : 'var(--danger)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            {uploadResult.ok ? <CheckCircle2 size={18} /> : <XCircle size={18} />}
            {uploadResult.ok
              ? `Upload terminé — ${uploadResult.uploaded ?? 0} envoyé(s), ${uploadResult.skipped ?? 0} ignoré(s), ${uploadResult.errors ?? 0} erreur(s)`
              : `Erreur : ${uploadResult.error}`}
          </div>
        )}
      </section>

      {uploadEvents.length > 0 && (
        <section style={cardStyle}>
          <h2 style={sectionTitleStyle}>Journal d'exécution</h2>
          <div
            style={{
              maxHeight: '360px',
              overflowY: 'auto',
              background: 'var(--glass)',
              borderRadius: 'var(--radius-md)',
              padding: '0.75rem',
              fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
              fontSize: '0.82rem',
            }}
          >
            {uploadEvents.map((e, i) => {
              const style = EVENT_STYLES[e.type] ?? { color: 'var(--text)', icon: FileText };
              const Icon = style.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    padding: '0.3rem 0',
                    borderBottom: '1px solid var(--glass-border)',
                    color: style.color,
                  }}
                >
                  <Icon size={14} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div style={{ flex: 1 }}>
                    <strong>{e.type}</strong>
                    {e.fileName && ` — ${e.fileName}`}
                    {e.total != null && e.done != null && e.type !== 'completed' && (
                      <span style={{ color: 'var(--text-muted)' }}> ({e.done}/{e.total})</span>
                    )}
                    {e.type === 'completed' && (
                      <span style={{ color: 'var(--text-muted)' }}>
                        {' '}— {e.uploaded} envoyé(s), {e.skipped} ignoré(s), {e.errors} erreur(s)
                      </span>
                    )}
                    {e.error && <div style={{ color: 'var(--danger)' }}>{e.error}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirmer l'upload Drive"
        footer={
          <>
            <button onClick={() => setConfirmOpen(false)} style={secondaryButtonStyle}>
              Annuler
            </button>
            <button onClick={handleUpload} style={primaryButtonStyle(false)}>
              <Cloud size={16} /> Lancer l'upload
            </button>
          </>
        }
      >
        <p>
          Upload des CRA du job sélectionné vers le dossier{' '}
          <strong>{folderName}</strong>.
        </p>
        {replaceExisting && (
          <p style={{ color: 'var(--warning)' }}>
            ⚠️ Les fichiers PDF de même nom déjà présents sur Drive seront <strong>remplacés</strong>.
          </p>
        )}
      </Modal>
    </div>
  );
}

// Styles partagés (mêmes que GenerationView)
const cardStyle = {
  background: 'var(--card-bg)',
  padding: '1.5rem',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-card)',
};
const sectionTitleStyle = { margin: '0 0 1rem 0', fontSize: '1rem', color: 'var(--primary)' };
const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.25rem',
  color: 'var(--text-muted)',
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '0.5rem 0.75rem',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--glass-border)',
  background: 'var(--glass)',
  color: 'var(--text)',
  fontSize: '0.9rem',
};
const primaryButtonStyle = (disabled) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 1.25rem',
  background: disabled ? 'var(--text-muted)' : 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
});
const secondaryButtonStyle = {
  padding: '0.6rem 1.25rem',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: '0.9rem',
};
const dangerButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.6rem 1.25rem',
  background: 'var(--danger)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 600,
};
